import 'theme';

class EverlastingJervisRules extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const variants = JSON.parse(this.querySelector('script[data-everlasting-jervis-variants]').textContent);
    const scope = this.closest('.shopify-section');
    const update = () => {
      if (!this.isConnected || !scope) return;
      scope.querySelectorAll(`variant-picker[handle="${CSS.escape(this.dataset.productHandle)}"]`).forEach(picker => {
        const style = picker.querySelector('input[data-option-position="1"]:checked')?.dataset.optionLabel;
        if (!style) return;
        const available = new Set(variants.filter(variant => variant.available && variant.options[0] === style).map(variant => variant.options[1]));
        picker.querySelectorAll('input[data-option-position="2"]').forEach(input => {
          const disabled = !available.has(input.dataset.optionLabel);
          input.disabled = disabled;
          const label = picker.querySelector(`label[for="${CSS.escape(input.id)}"]`);
          if (label) {
            label.hidden = false;
            label.classList.toggle('is-disabled', disabled);
            label.toggleAttribute('data-conditional-unavailable', disabled);
          }
        });
      });
    };
    const schedule = () => queueMicrotask(update);
    scope?.addEventListener('change', schedule, { signal: this.controller.signal });
    scope?.addEventListener('product:rerender', schedule, { capture: true, signal: this.controller.signal });
    schedule();
  }
  disconnectedCallback() { this.controller.abort(); }
}
if (!customElements.get('everlasting-jervis-rules')) customElements.define('everlasting-jervis-rules', EverlastingJervisRules);

document.addEventListener('change', event => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const card = input.closest('product-card[data-everlasting-variant-card]');
  const id = input.dataset.variantId;
  if (!card || !id) return;
  const state = card.querySelector(`template[data-everlasting-card-state="${CSS.escape(id)}"]`);
  if (!state) return;
  const title = card.querySelector('.product-title');
  if (title) title.textContent = state.dataset.title;
  const price = state.content.querySelector('price-list');
  if (price) card.querySelector('price-list')?.replaceWith(price.cloneNode(true));
  card.querySelectorAll('input[name="id"]').forEach(field => {
    field.value = id;
    field.dispatchEvent(new Event('change', { bubbles: true }));
  });
  card.querySelectorAll('button[type="submit"]').forEach(button => { button.disabled = state.dataset.available !== 'true'; });
});

(function () {
  function pauseClosedAccordionVideos(details) {
    if (!(details instanceof HTMLDetailsElement) || !details.matches('accordion-disclosure > details')) return;
    if (details.open && details.getAttribute('aria-expanded') !== 'false') return;
    details.querySelectorAll('video-media').forEach(video => {
      // Queue pause even while the player is loading and has no playing attribute yet.
      video.player.pause();
    });
  }

  // V11 sets aria-expanded=false before its closing animation removes open.
  new MutationObserver(records => {
    const disclosures = new Set(records.filter(record => record.oldValue !== record.target.getAttribute(record.attributeName)).map(record => record.target));
    disclosures.forEach(pauseClosedAccordionVideos);
  }).observe(document.body, { attributes: true, subtree: true, attributeOldValue: true, attributeFilter: ['aria-expanded', 'open'] });

  document.addEventListener('media:play', event => {
    if (event.target instanceof Element && event.target.matches('video-media')) {
      pauseClosedAccordionVideos(event.target.closest('accordion-disclosure > details'));
    }
  });
})();

(function () {
  const noteSelector = '#slidecarthq .note-body textarea, textarea[name="note"], textarea[name*="gift"][name*="note"], input[name*="gift"][name*="note"], #gift-note, .gift-note-textarea, .gift-note textarea, .gift-note input[type="text"]';
  const initializedNotes = new WeakSet();

  function limitNote(field) {
    if (initializedNotes.has(field)) return;
    initializedNotes.add(field);
    field.setAttribute('maxlength', '350');
    const isCartNote = field.matches('#slidecarthq .note-body textarea');
    if (!isCartNote && field.name === 'note') return;
    const counter = document.createElement('span');
    counter.className = isCartNote ? 'custom-sc' : 'gift-note-counter';
    const update = () => {
      counter.textContent = isCartNote ? `${350 - field.value.length} characters left` : `${field.value.length}/350 characters`;
      counter.classList.toggle('is-near-limit', !isCartNote && field.value.length >= 315);
    };
    field.insertAdjacentElement('afterend', counter);
    field.addEventListener('input', update);
    update();
  }

  function applyNoteLimits(scope) {
    if (scope instanceof Element && scope.matches(noteSelector)) limitNote(scope);
    if (scope.shadowRoot) applyNoteLimits(scope.shadowRoot);
    if (scope instanceof ShadowRoot && !observedNoteRoots.has(scope)) {
      observedNoteRoots.add(scope);
      noteObserver.observe(scope, { childList: true, subtree: true });
    }
    scope.querySelectorAll?.(noteSelector).forEach(limitNote);
    scope.querySelectorAll?.('*').forEach(element => {
      if (element.shadowRoot) applyNoteLimits(element.shadowRoot);
    });
  }

  const observedNoteRoots = new WeakSet();
  const noteObserver = new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (node instanceof Element && !node.matches('.custom-sc, .gift-note-counter')) applyNoteLimits(node);
      }
    }
  });
  noteObserver.observe(document.body, { childList: true, subtree: true });
  applyNoteLimits(document);

  // App widgets may attach a shadow root after inserting their host element.
  let noteScanTimeout;
  const scheduleNoteScan = () => {
    clearTimeout(noteScanTimeout);
    noteScanTimeout = setTimeout(() => applyNoteLimits(document), 400);
  };
  scheduleNoteScan();
  document.addEventListener('click', event => {
    if (event.composedPath().some(node => node instanceof Element && node.matches('.note-trigger'))) scheduleNoteScan();
  });
  document.addEventListener('everlasting:cart-opened', () => applyNoteLimits(document));
  document.addEventListener('shopify:section:load', event => {
    applyNoteLimits(event.target);
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  });
})();
